import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CALENDAR_DAYS, DAYS_PER_WEEK } from '../../constants/date';
import {
  PRIVATE_ROOM_BASE_DATE_BOUNDARY_HOUR,
  SHOWER_BASE_DATE_BOUNDARY_HOUR,
} from '../../constants/reservation';
import { SPACE_TYPE } from '../../constants/space';
import {
  addCalendarDays,
  createJstCalendarDate,
  getBusinessDate,
  getJstDateInfo,
  getReservationBaseDate,
  isAfterCalendarDate,
  isBeforeCalendarDate,
  isSameCalendarDate,
  type JstCalendarDate,
} from '../../utils/businessDate';
import { toDateString } from '../../utils/datetime';

interface DatePickerProps {
  selectedDate?: `${number}-${number}-${number}` | null; // YYYY-MM-DD形式
  onDateSelect: (date: string) => void;
  className?: string;
  maxDaysFromToday: number; // 当日から何日先まで選択可能か（0 = 当日のみ、1 = 翌日まで、7 = 7日先まで）
  airport?: string;
  flightType?: string;
  terminal?: string;
  spaceType: (typeof SPACE_TYPE)[keyof typeof SPACE_TYPE];
  currentMonth: JstCalendarDate;
  changeMonth: (direction: number) => void;
  setCurrentMonth: (date: JstCalendarDate) => void;
}

export const DatePicker = ({
  selectedDate,
  onDateSelect,
  className = 'date-picker date-picker__booth',
  maxDaysFromToday,
  spaceType,
  airport,
  flightType,
  terminal,
  currentMonth,
  changeMonth,
  setCurrentMonth,
}: DatePickerProps) => {
  const { t, i18n } = useTranslation();
  const [isCalendarOpen, setIsCalendarOpen] = useState(true);

  useEffect(() => {
    if (selectedDate) {
      // new Date('YYYY-MM-DD') は UTC 解釈されるため、ブラウザTZがUTC以西だと月初選択で前月にロールバックする
      const [year, month] = selectedDate.split('-').map(Number);
      setCurrentMonth(createJstCalendarDate(year, month, 1));
    }
  }, [selectedDate, setCurrentMonth]);

  // 月のラベル（例: 2025年11月 / November 2025）
  const getMonthLabel = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth() + 1;

    if (i18n.language === 'ja') {
      return `${year}年${month}月`;
    } else {
      const monthNames = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ];
      return `${monthNames[currentMonth.getMonth()]} ${year}`;
    }
  };

  // カレンダーの日付配列を生成
  // 注: getReservationBaseDate / getJstDateInfo は内部で Intl.DateTimeFormat.formatToParts を呼ぶため、
  // ループ内で呼ぶと最大42回実行されることになる。境界値はループ外で1度だけ算出して使い回す。
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    // 月の最初の日と最後の日
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    // 最初の日の曜日（0: 日曜日）
    const firstDayOfWeek = firstDay.getDay();

    // カレンダーに表示する日数
    const daysInMonth = lastDay.getDate();

    // ループ前に1度だけ計算する境界値
    const reservationBaseDate = getReservationBaseDate(
      spaceType,
      airport,
      flightType,
      terminal,
    );
    const {
      year: nowYear,
      month: nowMonth,
      day: nowDay,
      hour: jstHour,
    } = getJstDateInfo(new Date());
    const boundaryHour =
      spaceType === SPACE_TYPE.PRIVATE_ROOM
        ? PRIVATE_ROOM_BASE_DATE_BOUNDARY_HOUR
        : SHOWER_BASE_DATE_BOUNDARY_HOUR;
    const futureBaseDate = createJstCalendarDate(nowYear, nowMonth, nowDay);
    const offsetByBoundary = jstHour >= boundaryHour ? 1 : 0;
    const futureMaxDate = addCalendarDays(
      futureBaseDate,
      offsetByBoundary + maxDaysFromToday,
    );

    const isPast = (date: JstCalendarDate) =>
      isBeforeCalendarDate(date, reservationBaseDate);
    const isFuture = (date: JstCalendarDate) =>
      isAfterCalendarDate(date, futureMaxDate);

    const days: Array<{
      date: string;
      day: number;
      isCurrentMonth: boolean;
      isToday: boolean;
      isSelected: boolean;
      isPast: boolean;
    }> = [];

    // 前月の日付で埋める
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const day = prevMonthLastDay - i;
      const date = createJstCalendarDate(year, month, day);
      const dateStr = toDateString(date);
      days.push({
        date: dateStr,
        day,
        isCurrentMonth: false,
        isToday: false,
        isSelected: dateStr === selectedDate,
        isPast: isPast(date) || isFuture(date),
      });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = createJstCalendarDate(year, month + 1, day);
      const dateStr = toDateString(date);

      days.push({
        date: dateStr,
        day,
        isCurrentMonth: true,
        isToday: isSameCalendarDate(date, reservationBaseDate),
        isSelected: dateStr === selectedDate,
        isPast: isPast(date) || isFuture(date),
      });
    }

    // 次月の日付で埋める（カレンダーを6週間分表示）
    const remainingDays = CALENDAR_DAYS - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      const date = createJstCalendarDate(year, month + 2, day);
      const dateStr = toDateString(date);
      days.push({
        date: dateStr,
        day,
        isCurrentMonth: false,
        isToday: false,
        isSelected: dateStr === selectedDate,
        isPast: isPast(date) || isFuture(date),
      });
    }

    return days;
  };

  // 日付をクリック
  const handleDateClick = (dateStr: string, isPast: boolean) => {
    if (isPast) return;
    onDateSelect(dateStr);
  };

  const calendarDays = generateCalendarDays();

  // 選択された日付または今日の日付（営業日）がある週のインデックスを計算
  const getSelectedWeekIndex = (): number => {
    const targetDate = selectedDate || toDateString(getBusinessDate());
    const index = calendarDays.findIndex((day) => day.date === targetDate);
    if (index === -1) return 0;
    return Math.floor(index / DAYS_PER_WEEK);
  };

  const selectedWeekIndex = getSelectedWeekIndex();

  // 週表示モードの場合、選択された週の日付だけを取得
  const getDisplayDays = () => {
    if (isCalendarOpen) {
      return calendarDays;
    }
    // 選択された週の日付だけを返す（7日分）
    const startIndex = selectedWeekIndex * DAYS_PER_WEEK;
    return calendarDays.slice(startIndex, startIndex + DAYS_PER_WEEK);
  };

  const displayDays = getDisplayDays();

  const pickerClassName = `${className}${!isCalendarOpen ? ' date-picker--week-view' : ''}`;

  return (
    <div className={pickerClassName} data-date-input-id="reserve-date">
      <div className="date-picker__header">
        <button
          className="date-picker__nav date-picker__nav--prev"
          type="button"
          onClick={() => changeMonth(-1)}
          aria-label={t('common.datePicker.previousMonth')}
        >
          ←
        </button>
        <div className="date-picker__month-label" aria-live="polite">
          {getMonthLabel()}
        </div>
        <button
          className="date-picker__nav date-picker__nav--next"
          type="button"
          onClick={() => changeMonth(1)}
          aria-label={t('common.datePicker.nextMonth')}
        >
          →
        </button>
      </div>

      <div className="date-picker__weekdays">
        <span>{t('common.weekdays.sunday')}</span>
        <span>{t('common.weekdays.monday')}</span>
        <span>{t('common.weekdays.tuesday')}</span>
        <span>{t('common.weekdays.wednesday')}</span>
        <span>{t('common.weekdays.thursday')}</span>
        <span>{t('common.weekdays.friday')}</span>
        <span>{t('common.weekdays.saturday')}</span>
      </div>

      <div
        className="date-picker__grid"
        aria-label={t('common.datePicker.calendar')}
      >
        {displayDays.map((dayInfo, index) => {
          // 当月以外でも、選択可能な日付（isPastがfalse）であれば有効にする
          const isDisabled = dayInfo.isPast;
          return (
            <button
              key={index}
              type="button"
              className={`date-picker__day-button ${
                dayInfo.isToday ? 'is-today' : ''
              } ${dayInfo.isSelected ? 'is-selected' : ''} ${
                isDisabled ? 'is-disabled' : ''
              }`}
              onClick={() => handleDateClick(dayInfo.date, isDisabled)}
              disabled={isDisabled}
              aria-label={dayInfo.date}
            >
              {dayInfo.day}
            </button>
          );
        })}
      </div>

      <button
        className="date-picker__toggle"
        type="button"
        onClick={() => setIsCalendarOpen(!isCalendarOpen)}
        aria-hidden="true"
      >
        <span className="date-picker__toggle-icon">⌃</span>
      </button>
    </div>
  );
};
